import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import useConfirm from '../../../hooks/useConfirm';
import { addFaq, editFaq, removeFaq } from '../../../helpers/faqs';
import { DraftFaq } from '../../../types/faqs';
import {
  FAQ_HEADER_MAX_LENGTH,
  FAQ_MAX_ITEMS,
  FAQ_TEXT_MAX_LENGTH,
} from '../../../constants/faqs';

interface FaqSectionProps {
  faqs: DraftFaq[];
  onChange: (faqs: DraftFaq[]) => void;
  disabled?: boolean;
  showErrors?: boolean;
}

// filter.css pins every outlined root to 54px, which a multiline field outgrows
const autoHeight = { '& .MuiOutlinedInput-root': { height: 'auto' } };

function FaqSection({ faqs, onChange, disabled, showErrors }: FaqSectionProps) {
  const onConfirm = useConfirm();
  const isAtCap = faqs.length >= FAQ_MAX_ITEMS;

  const onRemove = async (id: string) => {
    const confirmed = await onConfirm({
      title: 'Remove this FAQ?',
      message: 'This removes it from your booking site.',
      positiveText: 'Remove',
      negativeText: 'Keep',
    });
    if (confirmed) onChange(removeFaq(faqs, id));
  };

  if (!faqs.length) {
    return (
      <Box
        sx={{
          textAlign: 'center',
          py: 4,
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 2,
        }}>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 1.5 }}>
          No FAQs yet
        </Typography>
        <Button
          variant="outlined"
          disabled={disabled}
          onClick={() => onChange(addFaq(faqs))}>
          Add FAQ
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {faqs.map((faq, index) => (
          <Box
            key={faq.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1.5,
            }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
              <Typography variant="caption" color="text.secondary">
                FAQ {index + 1}
              </Typography>
              <IconButton
                size="small"
                disabled={disabled}
                onClick={() => onRemove(faq.id)}
                aria-label="Remove FAQ"
                sx={{ mr: -0.5 }}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              label="Question"
              fullWidth
              multiline
              maxRows={3}
              size="small"
              sx={autoHeight}
              slotProps={{ htmlInput: { maxLength: FAQ_HEADER_MAX_LENGTH } }}
              disabled={disabled}
              value={faq.header}
              error={showErrors && !faq.header.trim()}
              helperText={
                showErrors && !faq.header.trim() ? 'Required' : undefined
              }
              onChange={event =>
                onChange(
                  editFaq(faqs, faq.id, {
                    header: event.target.value.replace(/\n/g, ' '),
                  }),
                )
              }
            />
            <TextField
              label="Answer"
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={autoHeight}
              slotProps={{ htmlInput: { maxLength: FAQ_TEXT_MAX_LENGTH } }}
              disabled={disabled}
              value={faq.text}
              error={showErrors && !faq.text.trim()}
              helperText={
                showErrors && !faq.text.trim() ? 'Required' : undefined
              }
              onChange={event =>
                onChange(editFaq(faqs, faq.id, { text: event.target.value }))
              }
            />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
        <Typography variant="body2" color="text.secondary">
          {faqs.length} of {FAQ_MAX_ITEMS} FAQs
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          disabled={disabled || isAtCap}
          onClick={() => onChange(addFaq(faqs))}>
          Add FAQ
        </Button>
      </Box>
    </Box>
  );
}

export default FaqSection;
